import React from 'react';
import Typography from '../Text/Typography';
import Icon from '../Icon/Icon';
import Tag from '../Tag/Tag';
import IconButton from '../Button/IconButton';
import Banner from '../Banner/Banner';
import ContentfulImage from '../ContentfulImage/ContentfulImage';
import type { ProductDetailProduct } from '../ProductDetailContent/ProductDetailContent';
import './RoutineCard.css';

const capitalizeWords = (str: string) => {
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface RoutineCardProps {
  company?: string;
  productName?: string;
  category?: string;
  imageSrc?: string;
  onDelete?: () => void;
  onDragStart?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  showMobileControls?: boolean;
  showOrderWarning?: boolean;
  expectedPosition?: number;
  currentPosition?: number;
  /** Full product for detail view; when provided with onClick, card main area is clickable */
  product?: ProductDetailProduct;
  onClick?: (product: ProductDetailProduct) => void;
}

const RoutineCard: React.FC<RoutineCardProps> = ({
  company = 'COMPANY',
  productName = 'Product Name',
  category = 'Cleanser',
  imageSrc,
  onDelete,
  onDragStart,
  onDragEnd,
  onMoveUp,
  onMoveDown,
  canMoveUp = true,
  canMoveDown = true,
  showMobileControls = false,
  showOrderWarning = false,
  expectedPosition,
  currentPosition,
  product,
  onClick,
}) => {
  const isClickable = Boolean(product && onClick);

  const mainContent = (
    <>
      {/* Drag Handle - only this area starts drag */}
      <div
        className="routine-card-drag-handle"
        draggable={!!onDragStart}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        role={onDragStart ? 'button' : undefined}
        aria-label={onDragStart ? 'Drag to reorder' : undefined}
      >
        <Icon name="drag" size="large" />
      </div>

      {/* Image + Content: clickable when product + onClick provided */}
      {isClickable && product ? (
        <button
          type="button"
          className="routine-card-clickable-area"
          onClick={() => onClick(product)}
          aria-label={`View details for ${productName}`}
        >
          <div className="routine-card-image">
            {imageSrc ? (
              <ContentfulImage
                src={imageSrc}
                alt={productName}
                width={80}
                height={80}
                fit="fill"
                quality={80}
                format="webp"
              />
            ) : (
              <div className="routine-card-image-placeholder" />
            )}
          </div>
          <div className="routine-card-content">
            <div className="routine-card-texts">
              <Typography variant="body-small-bold" className="routine-card-company">
                {company}
              </Typography>
              <Typography variant="body-bold" className="routine-card-name">
                {productName}
              </Typography>
            </div>
            {category && (
              <Tag>{capitalizeWords(category)}</Tag>
            )}
          </div>
        </button>
      ) : (
        <>
          <div className="routine-card-image">
            {imageSrc ? (
              <ContentfulImage
                src={imageSrc}
                alt={productName}
                width={80}
                height={80}
                fit="fill"
                quality={80}
                format="webp"
              />
            ) : (
              <div className="routine-card-image-placeholder" />
            )}
          </div>
          <div className="routine-card-content">
            <div className="routine-card-texts">
              <Typography variant="body-small-bold" className="routine-card-company">
                {company}
              </Typography>
              <Typography variant="body-bold" className="routine-card-name">
                {productName}
              </Typography>
            </div>
            {category && (
              <Tag>{capitalizeWords(category)}</Tag>
            )}
          </div>
        </>
      )}

      {/* Mobile Reordering Controls */}
      {showMobileControls && (
        <div className="routine-card-mobile-controls">
          <IconButton
            icon={<Icon name="chevron-up" size="small" />}
            onClick={onMoveUp}
            disabled={!canMoveUp}
            ariaLabel="Move up"
            className="mobile-reorder-button"
          />
          <IconButton
            icon={<Icon name="chevron-down" size="small" />}
            onClick={onMoveDown}
            disabled={!canMoveDown}
            ariaLabel="Move down"
            className="mobile-reorder-button"
          />
        </div>
      )}

      {/* Delete Icon Button */}
      {onDelete && (
        <IconButton
          icon={<Icon name="trash" size="large" />}
          onClick={onDelete}
          ariaLabel="Delete product"
        />
      )}
    </>
  );

  return (
    <div className="routine-card">
      <div className="routine-card-main">
        {mainContent}
      </div>

      {showOrderWarning && (
        <div className="routine-card-order-warning">
          <Banner variant="caution" title="Order Warning">
            This {category ? category.toLowerCase() : 'product'} should be used in position {expectedPosition ?? '?'} for optimal results.
          </Banner>
        </div>
      )}
    </div>
  );
};

export default RoutineCard;
